import * as Yup from "yup";

import {
  AaaGameInformationAvailabilityDataType,
  AaaGameInformationConceptDetailsDataType,
  AaaGameInformationConfigurationsDataType,
  AaaGameInformationDistributionInformationDataType,
  AaaGameInformationGameExpectationsDataType,
  AaaGameInformationSubFormsDataTypes,
  AaaGameInformationSupplementaryDataType,
  AaaGameInformationTechnicalCapabilitiesDataType,
  AaaGameInformationUgcDataType,
  AaaGameInformationXboxNetworkDataType,
  GameInformationConceptDetailsDataType,
  GameInformationConfigurationsDataType,
  GameInformationGameExpectationsDataType,
  GameInformationPricingAndAvailabilityDataType,
  GameInformationSubFormsDataTypes,
  GameInformationSupplementaryDataType,
  GameInformationTechnicalCapabilitiesDataType,
  GameInformationUgcDataType,
  GameInformationXboxNetworkDataType,
  IdAzureIntroContactDataType,
  IdXboxIntroContactDataType,
  IdXboxIntroStudioDataType,
  IdXboxIntroSubFormsDataTypes,
  PitchInformationAboutYourGameDataType,
  PitchInformationAboutYourselfDataType,
  PitchInformationAskForXboxDataType,
  PitchInformationPublisherDeveloperDataType,
} from "../../models/forms";
import { StreamingEuRequest } from "../../pages/StreamingEuPage/models";
import {
  aaaGameInformationValidationSchemas,
  idAzureIntroValidationSchemas,
  idXboxGameInformationValidationSchemas,
  idXboxIntroValidationSchemas,
  pitchInformationValidationSchemas,
  streamingEuValidationSchemas,
} from "./formValidationSchemas";

/* eslint-disable @typescript-eslint/no-explicit-any */
const isFormValid = async (
  form:
    | IdXboxIntroSubFormsDataTypes
    | GameInformationSubFormsDataTypes
    | PitchInformationPublisherDeveloperDataType
    | PitchInformationAboutYourGameDataType
    | PitchInformationAskForXboxDataType
    | PitchInformationAboutYourselfDataType
    | AaaGameInformationSubFormsDataTypes
    | StreamingEuRequest,
  schema: any
) => {
  if (!form) {
    return false;
  }
  const yupObject = Yup.object().shape(schema);
  const isValid = await yupObject.isValid(form);
  return isValid;
};

/** ************************************* ID@Xbox Introduction Validation Helpers **************************************************/
export const isIdXboxIntroContactFormValid = async (contact: IdXboxIntroContactDataType) =>
  isFormValid(contact, idXboxIntroValidationSchemas({}).contact);

export const isIdXboxIntroStudioFormValid = async (studio: IdXboxIntroStudioDataType) =>
  isFormValid(studio, idXboxIntroValidationSchemas({}).studio);

/** ************************************* ID@Xbox Game Information Validation Helpers **************************************************/
export const isIdXboxGameInformationDetailsFormValid = async (
  details: GameInformationConceptDetailsDataType
) => isFormValid(details, idXboxGameInformationValidationSchemas({}).details);

export const isIdXboxGameInformationGameExpectationsValid = async (
  gameExpectations: GameInformationGameExpectationsDataType
) => isFormValid(gameExpectations, idXboxGameInformationValidationSchemas({}).gameExpectations);

export const isIdXboxGameInformationPricingAndAvailabilityFormValid = async (
  pricingAndAvailability: GameInformationPricingAndAvailabilityDataType
) =>
  isFormValid(
    pricingAndAvailability,
    idXboxGameInformationValidationSchemas({}).pricingAndAvailability
  );

export const isIdXboxGameInformationTechnicalCapabilitiesFormValid = async (
  technicalCapabilities: GameInformationTechnicalCapabilitiesDataType
) =>
  isFormValid(
    technicalCapabilities,
    idXboxGameInformationValidationSchemas({}).technicalCapabilities
  );

export const isIdXboxGameInformationConfigurationsFormValid = async (
  configurations: GameInformationConfigurationsDataType
) => isFormValid(configurations, idXboxGameInformationValidationSchemas({}).configurations);

export const isIdXboxGameInformationXboxNetworkFormValid = async (
  xboxNetwork: GameInformationXboxNetworkDataType
) => isFormValid(xboxNetwork, idXboxGameInformationValidationSchemas({}).xboxNetwork);

export const isIdXboxGameInformationUgcFormValid = async (ugc: GameInformationUgcDataType) =>
  isFormValid(ugc, idXboxGameInformationValidationSchemas({}).ugc);

export const isIdXboxGameInformationSupplementaryDataFormValid = async (
  supplementaryData: GameInformationSupplementaryDataType
) => isFormValid(supplementaryData, idXboxGameInformationValidationSchemas({}).supplementaryData);

/** ************************************* AAA Game Information Validation Helpers **************************************************/
export const isAaaGameInformationDetailsFormValid = async (
  details: AaaGameInformationConceptDetailsDataType
) => isFormValid(details, aaaGameInformationValidationSchemas({}).details);

export const isAaaGameInformationDistributionInformationFormValid = async (
  distributionInformation: AaaGameInformationDistributionInformationDataType
) =>
  isFormValid(
    distributionInformation,
    aaaGameInformationValidationSchemas({}).distributionInformation
  );

export const isAaaGameInformationGameExpectationsValid = async (
  gameExpectations: AaaGameInformationGameExpectationsDataType
) => isFormValid(gameExpectations, aaaGameInformationValidationSchemas({}).gameExpectations);

export const isAaaGameInformationAvailabilityFormValid = async (
  availability: AaaGameInformationAvailabilityDataType
) => isFormValid(availability, aaaGameInformationValidationSchemas({}).availability);

export const isAaaGameInformationTechnicalCapabilitiesFormValid = async (
  technicalCapabilities: AaaGameInformationTechnicalCapabilitiesDataType
) =>
  isFormValid(technicalCapabilities, aaaGameInformationValidationSchemas({}).technicalCapabilities);

export const isAaaGameInformationConfigurationsFormValid = async (
  configurations: AaaGameInformationConfigurationsDataType
) => isFormValid(configurations, aaaGameInformationValidationSchemas({}).configurations);

export const isAaaGameInformationXboxNetworkFormValid = async (
  xboxNetwork: AaaGameInformationXboxNetworkDataType
) => isFormValid(xboxNetwork, aaaGameInformationValidationSchemas({}).xboxNetwork);

export const isAaaGameInformationUgcFormValid = async (ugc: AaaGameInformationUgcDataType) =>
  isFormValid(ugc, aaaGameInformationValidationSchemas({}).ugc);

export const isAaaGameInformationSupplementaryDataFormValid = async (
  supplementaryData: AaaGameInformationSupplementaryDataType
) => isFormValid(supplementaryData, aaaGameInformationValidationSchemas({}).supplementaryData);

/** ************************************* ID@Xbox, ID@Azure Introduction Validation Helpers **************************************************/
export const isIdAzureIntroContactAndStudioFormValid = async (
  contactAndStudio: IdAzureIntroContactDataType
) => isFormValid(contactAndStudio, idAzureIntroValidationSchemas({}).contactAndStudio);

/** *********************************** Pitch Validation Helpers /******************************* */
export const isPitchInformationPublisherDeveloperValid = async (
  publisherDeveloper: PitchInformationPublisherDeveloperDataType
) => isFormValid(publisherDeveloper, pitchInformationValidationSchemas({}).publisherDeveloper);

export const isPitchAboutYourGameValid = async (
  aboutYourGame: PitchInformationAboutYourGameDataType
) => isFormValid(aboutYourGame, pitchInformationValidationSchemas({}).aboutYourGame);

export const isPitchAskForXboxValid = async (askForXbox: PitchInformationAskForXboxDataType) =>
  isFormValid(askForXbox, pitchInformationValidationSchemas({}).askForXbox);

export const isPitchAboutYourselfValid = async (
  aboutYourself: PitchInformationAboutYourselfDataType
) => isFormValid(aboutYourself, pitchInformationValidationSchemas({}).aboutYourself);
/** ************************************* Streaming EU Validation Helpers **************************************************/
export const isStreamingEuFormValid = async (streamingEuData: StreamingEuRequest) =>
  isFormValid(streamingEuData, streamingEuValidationSchemas());
